import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import {
  Avatar,
  Button,
  IconCheck,
  IconPlus,
  IconTransferTruckTime,
  Input,
  Modal,
  ScreenLayout,
  SkeletonAvatar,
  SkeletonBlock,
  SkeletonListRows,
  StatusBadge,
  usePendingAppAction,
  usePref,
} from '@taki/ui';
import { useTakiApp } from '../../App.js';
import {
  CAT_TYPE,
  DEFAULT_REASONS,
  OUTING_TYPE,
  assetToCat,
  custodyToOuting,
  formatDateTime,
  isActive,
  isOngoing,
  isOverdue,
  isType,
  newCatCustomFields,
  nowTime,
  reasonLabelOf,
  todayIso,
  toDateTime,
  type Cat,
  type Outing,
  type ReasonOption,
} from './data.js';
import {
  DEFAULT_CUSTOM_REASONS,
  DEFAULT_STATS,
  REASONS_PREF_KEY,
  STATS_MAX,
  STATS_PREF_KEY,
  STAT_DEFS,
  type OutingStatId,
} from './settings-defs.js';

/**
 * Ecran unique de l'app Cat Logger Outside.
 *
 * Un SEUL `ScreenLayout` (regle du gabarit : le menu SEGMENTE les donnees, il ne
 * multiplie pas les layouts). Deux sections pilotees par un state local :
 *  - `inside`  : les chats presents au refuge (onglet 1) ;
 *  - `outside` : les chats actuellement en sortie (onglet 2).
 * Les reglages de l'app (motifs de sortie, stats d'en-tete) vivent dans
 * settings.tsx, monte par le hub Parametres du shell (route /settings) ;
 * l'ecran LIT les prefs partagees (settings-defs.ts) via `usePref` et se
 * resynchronise en direct via l'event "taki:pref-change".
 * Les formulaires (nouvelle sortie, nouveau chat) s'ouvrent dans le composant
 * `Modal` de @taki/ui, jamais dans la zone centrale (regle G6).
 *
 * Donnees (cage IA) : 100% via `@taki/sdk` obtenu par `useTakiApp()`.
 *  - un chat  = `asset`   de type 'cat'           (partage avec cat-manager-six) ;
 *  - une sortie = `custody` de type 'outside-visit' (receivedAt = depart,
 *    dueBackAt = retour prevu, returnedAt = retour effectif).
 * Un chat est "en sortie" tant qu'une custody le concernant n'a pas de
 * `returnedAt`.
 */

/** Section active de l'ecran (le menu de la zone 1). */
type Section = 'inside' | 'outside';

/* Largeurs deterministes des barres (nom / sous-ligne) du skeleton "En sortie",
   cyclees par ligne : rendu stable d'un render a l'autre, comme SkeletonListRows
   du kit @taki/ui. */
const OUT_NAME_WIDTHS = ['40%', '32%', '46%', '36%'];
const OUT_SUB_WIDTHS = ['80%', '72%', '86%', '76%'];

/**
 * Skeleton sur mesure de l'onglet "En sortie".
 *
 * L'onglet "Au refuge" a la ligne de liste standard (avatar + nom + sous-ligne)
 * que reproduit deja `SkeletonListRows` du kit ; l'onglet "En sortie" AJOUTE a
 * droite un amas propre a cette vue (retour prevu + pastille de statut + bouton
 * "Marquer rentre"). Ce composant reprend donc la ligne standard et pose cet
 * amas, aux memes empreintes que le reel (boites text-sm/text-xs h-5/h-4 pour
 * le texte, h-8 pour la pastille et le bouton `sm`) : aucun saut de mise en page
 * quand le contenu reel remplace le skeleton.
 *
 * Il ne s'affiche que si la zone 5 charge alors que l'onglet actif est deja
 * "En sortie" (par exemple un "Reessayer" depuis l'etat erreur) ; le tout
 * premier chargement se fait toujours sur "Au refuge" et rend SkeletonListRows.
 */
function OutsideSkeletonRows({ rows = 8 }: { rows?: number }): JSX.Element {
  return (
    <div aria-hidden>
      {Array.from({ length: Math.max(1, rows) }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3.5 border-b border-border-soft px-5 py-2.5"
        >
          <SkeletonAvatar size={30} />
          <div className="min-w-0 flex-1">
            {/* nom : text-sm, boite de ligne 1.25rem (h-5) */}
            <span className="flex h-5 items-center">
              <span
                className="taki-skeleton block h-3.5 rounded-xs"
                style={{ width: OUT_NAME_WIDTHS[index % OUT_NAME_WIDTHS.length] }}
              />
            </span>
            {/* sous-ligne : text-xs, boite de ligne 1rem (h-4) */}
            <span className="flex h-4 items-center">
              <span
                className="taki-skeleton block h-2.5 rounded-xs"
                style={{ width: OUT_SUB_WIDTHS[index % OUT_SUB_WIDTHS.length] }}
              />
            </span>
          </div>
          {/* amas de droite : retour prevu + pastille + bouton, memes empreintes */}
          <div className="flex shrink-0 items-center gap-2.5">
            {/* "Retour prevu : ..." : text-xs, boite de ligne 1rem (h-4) */}
            <span className="flex h-4 items-center">
              <span className="taki-skeleton block h-2.5 rounded-xs" style={{ width: '8.5rem' }} />
            </span>
            {/* pastille de statut : h-8, rayon StatusBadge (0.5625rem) */}
            <SkeletonBlock w="5.75rem" h="2rem" rounded="0.5625rem" />
            {/* bouton "Marquer rentre" (sm) : h-8, rayon var(--radius-sm) */}
            <SkeletonBlock w="9rem" h="2rem" rounded="var(--radius-sm)" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CatLoggerOutsideScreen(): JSX.Element {
  const { sdk, config } = useTakiApp();

  // --- Etat de l'ecran -----------------------------------------------------
  const [section, setSection] = useState<Section>('inside');
  const [query, setQuery] = useState('');

  const [cats, setCats] = useState<Cat[]>([]);
  const [outings, setOutings] = useState<Outing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Formulaires (modals) ------------------------------------------------
  // Un SEUL modal ouvert a la fois par ecran (doctrine du composant Modal).
  const [openModal, setOpenModal] = useState<'outing' | 'cat' | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Action inline "Marquer rentre" : l'id de la sortie en cours de cloture (une a
  // la fois). Distinct de `saving` (soumission des modals) pour ne faire tourner
  // le spinner QUE sur la ligne cliquee, pas sur toutes.
  const [returningId, setReturningId] = useState<string | null>(null);

  const [formCatId, setFormCatId] = useState('');
  const [formReason, setFormReason] = useState(DEFAULT_REASONS[0]?.value ?? '');
  const [formLeftDate, setFormLeftDate] = useState(todayIso);
  const [formLeftTime, setFormLeftTime] = useState(nowTime);
  const [formBackDate, setFormBackDate] = useState('');
  const [formBackTime, setFormBackTime] = useState('');
  const [formPersonName, setFormPersonName] = useState('');
  const [formPersonPhone, setFormPersonPhone] = useState('');

  const [formCatName, setFormCatName] = useState('');

  // Prefs partagees avec settings.tsx (hub Parametres du shell) : stats
  // d'en-tete affichees (zone 3) et motifs de sortie ajoutes dans les reglages.
  // L'ecran LIT, le hub EDITE ; la resynchronisation est en direct.
  const [statIds] = usePref<string[]>(STATS_PREF_KEY, DEFAULT_STATS);
  const [customReasons] = usePref<ReasonOption[]>(REASONS_PREF_KEY, DEFAULT_CUSTOM_REASONS);

  // --- Chargement ----------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [assets, custodies] = await Promise.all([sdk.assets.list(), sdk.custodies.list()]);
      setCats(
        assets
          .filter((a) => isType(a, CAT_TYPE) && isActive(a))
          .map(assetToCat)
          .sort((a, b) => a.name.localeCompare(b.name, 'fr')),
      );
      setOutings(
        custodies.filter((c) => isType(c, OUTING_TYPE) && isActive(c)).map(custodyToOuting),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Erreur inconnue.');
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  useEffect(() => {
    void load();
  }, [load]);

  // --- Donnees derivees ----------------------------------------------------

  /** La sortie en cours de chaque chat (la plus recente si plusieurs). */
  const ongoingByCatId = useMemo(() => {
    const map = new Map<string, Outing>();
    for (const outing of outings) {
      if (!isOngoing(outing) || outing.catId === '') {
        continue;
      }
      const current = map.get(outing.catId);
      const isMoreRecent =
        current == null ||
        (outing.leftAt?.getTime() ?? 0) > (current.leftAt?.getTime() ?? 0);
      if (isMoreRecent) {
        map.set(outing.catId, outing);
      }
    }
    return map;
  }, [outings]);

  const catsInside = useMemo(
    () => cats.filter((cat) => !ongoingByCatId.has(cat.id)),
    [cats, ongoingByCatId],
  );
  const catsOutside = useMemo(
    () => cats.filter((cat) => ongoingByCatId.has(cat.id)),
    [cats, ongoingByCatId],
  );

  /**
   * Motifs proposes : ceux livres avec l'app + ceux deja utilises par une
   * sortie + ceux ajoutes dans les reglages (pref locale au poste).
   */
  const reasons = useMemo(() => {
    const map = new Map<string, ReasonOption>();
    for (const reason of DEFAULT_REASONS) {
      map.set(reason.value, reason);
    }
    for (const outing of outings) {
      if (outing.reason !== '' && !map.has(outing.reason)) {
        map.set(outing.reason, { value: outing.reason, label: outing.reason });
      }
    }
    for (const reason of customReasons) {
      if (!map.has(reason.value)) {
        map.set(reason.value, reason);
      }
    }
    return [...map.values()];
  }, [outings, customReasons]);

  // Instant de reference du rendu : recalcule a chaque passe (liste courte, cout
  // negligeable) plutot que memoise, pour que "en retard" ne se fige jamais.
  const now = new Date();
  const overdueCount = [...ongoingByCatId.values()].filter((o) => isOverdue(o, now)).length;

  /** Filtre de recherche applique a la section courante (par nom de chat). */
  const filterByQuery = useCallback(
    (list: Cat[]) => {
      const needle = query.trim().toLowerCase();
      if (needle === '') {
        return list;
      }
      return list.filter((cat) => cat.name.toLowerCase().includes(needle));
    },
    [query],
  );

  const visibleInside = useMemo(() => filterByQuery(catsInside), [filterByQuery, catsInside]);
  const visibleOutside = useMemo(() => filterByQuery(catsOutside), [filterByQuery, catsOutside]);

  // --- Actions -------------------------------------------------------------

  const resetOutingForm = useCallback(() => {
    setFormCatId('');
    setFormReason(DEFAULT_REASONS[0]?.value ?? '');
    setFormLeftDate(todayIso());
    setFormLeftTime(nowTime());
    setFormBackDate('');
    setFormBackTime('');
    setFormPersonName('');
    setFormPersonPhone('');
    setSaveError(null);
  }, []);

  const openOutingForm = useCallback(() => {
    resetOutingForm();
    setOpenModal('outing');
  }, [resetOutingForm]);

  const openCatForm = useCallback(() => {
    setSection('inside'); // le bouton "Ajouter un chat" appartient a l'onglet 1
    setFormCatName('');
    setSaveError(null);
    setOpenModal('cat');
  }, []);

  const closeModal = useCallback(() => {
    setOpenModal(null);
  }, []);

  // --- Action du dock (shortcut) --------------------------------------------
  // Le shell pose l'action (taki.app.json, champ `actions`) puis navigue ici ;
  // l'app ouvre le Modal correspondant et consomme. `actionId` repasse a null
  // apres consume() : l'effet ne rejoue pas, pas de double ouverture.
  const { actionId, consume } = usePendingAppAction('cat-logger-outside');
  useEffect(() => {
    if (actionId == null) {
      return;
    }
    // Les deux actions appartiennent a l'onglet "Au refuge" : on s'y place
    // d'abord (openCatForm le fait deja lui-meme).
    if (actionId === 'new-outing') {
      setSection('inside');
      openOutingForm();
    } else if (actionId === 'new-cat') {
      openCatForm();
    }
    consume();
  }, [actionId, consume, openOutingForm, openCatForm]);

  /** Cree la sortie : une custody 'outside-visit' rattachee au chat choisi. */
  const handleSaveOuting = useCallback(async () => {
    const leftAt = toDateTime(formLeftDate, formLeftTime);
    if (formCatId === '') {
      setSaveError('Choisis le chat qui sort.');
      return;
    }
    if (formReason === '') {
      setSaveError('Choisis un motif de sortie.');
      return;
    }
    if (leftAt == null) {
      setSaveError('Renseigne la date de sortie.');
      return;
    }
    const dueBackAt = toDateTime(formBackDate, formBackTime);
    if (dueBackAt != null && dueBackAt.getTime() < leftAt.getTime()) {
      setSaveError('Le retour prevu ne peut pas preceder la sortie.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await sdk.custodies.create({
        orgId: config.orgId,
        type: OUTING_TYPE,
        itemAssetId: formCatId,
        receivedAt: leftAt,
        ...(dueBackAt != null ? { dueBackAt } : {}),
        customFields: {
          reason: formReason,
          personName: formPersonName.trim(),
          personPhone: formPersonPhone.trim(),
        },
      });
      setOpenModal(null);
      resetOutingForm();
      setSection('outside'); // le chat vient de partir : on le montre la ou il est
      await load();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }, [
    formCatId,
    formReason,
    formLeftDate,
    formLeftTime,
    formBackDate,
    formBackTime,
    formPersonName,
    formPersonPhone,
    sdk,
    config.orgId,
    resetOutingForm,
    load,
  ]);

  /** Cloture la sortie : le retour effectif fait revenir le chat dans l'onglet 1. */
  const handleMarkReturned = useCallback(
    async (outing: Outing) => {
      setReturningId(outing.id);
      setSaveError(null);
      try {
        // Patch partiel : seul returnedAt est ecrit, le sac customFields (motif,
        // personne) n'est pas touche et reste donc intact.
        await sdk.custodies.update(outing.id, { returnedAt: new Date() });
        await load();
      } catch (cause) {
        setSaveError(cause instanceof Error ? cause.message : 'Mise a jour impossible.');
      } finally {
        setReturningId(null);
      }
    },
    [sdk, load],
  );

  /** Cree une fiche chat (asset 'cat'), visible aussi dans cat-manager-six. */
  const handleSaveCat = useCallback(async () => {
    const name = formCatName.trim();
    if (name === '') {
      setSaveError('Renseigne le nom du chat.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await sdk.assets.create({
        orgId: config.orgId,
        type: CAT_TYPE,
        name,
        customFields: newCatCustomFields(),
      });
      setOpenModal(null);
      setFormCatName('');
      await load();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }, [formCatName, sdk, config.orgId, load]);

  // --- Rendu ---------------------------------------------------------------

  // Valeurs de TOUTES les stats du catalogue, calculees des donnees reelles.
  // La pref (editee au hub Parametres) ne choisit que LESQUELLES afficher,
  // dans l'ordre du catalogue, plafonnees a STATS_MAX (garde-fou du contrat).
  const statValues: Record<OutingStatId, string> = {
    inside: String(catsInside.length),
    outside: String(catsOutside.length),
    overdue: String(overdueCount),
  };
  const headerStats = STAT_DEFS.filter((def) => statIds.includes(def.id))
    .slice(0, STATS_MAX)
    .map((def) => ({ value: statValues[def.id], label: def.label }));

  // L'etat vide ne depend JAMAIS d'un formulaire : le modal flotte au-dessus.
  const emptyBySection: Record<Section, boolean> = {
    inside: catsInside.length === 0,
    outside: catsOutside.length === 0,
  };

  const emptyTitleBySection: Record<Section, string> = {
    inside: cats.length === 0 ? 'Aucun chat enregistre' : 'Aucun chat au refuge',
    outside: 'Aucun chat en sortie',
  };

  const emptyHintBySection: Record<Section, string> = {
    inside:
      cats.length === 0
        ? 'Clique sur "Ajouter un chat".'
        : 'Tous les chats sont en sortie : va voir l\'onglet "En sortie".',
    outside:
      'Les sorties s\'enregistrent depuis l\'onglet "Au refuge", bouton "Ajouter".',
  };

  return (
    <>
      <ScreenLayout
        appName="Cat Logger Outside"
        appIcon={<IconTransferTruckTime size={18} />}
        menuItems={[
          { id: 'inside', label: 'Au refuge', count: catsInside.length },
          { id: 'outside', label: 'En sortie', count: catsOutside.length },
        ]}
        activeMenuId={section}
        onMenuSelect={(id) => setSection(id === 'outside' ? 'outside' : 'inside')}
        title="Chats en sortie"
        headerStats={headerStats}
        onSearchChange={setQuery}
        searchValue={query}
        searchPlaceholder="Rechercher un chat"
        // Les boutons d'action appartiennent a l'entree de menu ACTIVE (regle G7) :
        // l'onglet "En sortie" n'en a aucun, la zone 6 ne s'y rend pas.
        quickActions={
          section === 'inside'
            ? [
                // Contrat v2 : l'action principale se declare `kind: 'add'`, le
                // gabarit impose "Ajouter", IconPlus et le lime (regle G5).
                {
                  id: 'new-outing',
                  label: 'Ajouter',
                  kind: 'add',
                  disabled: loading || error != null || catsInside.length === 0,
                  onClick: openOutingForm,
                },
                {
                  id: 'new-cat',
                  label: 'Ajouter un chat',
                  icon: <IconPlus size={15} />,
                  disabled: loading || error != null,
                  onClick: openCatForm,
                },
              ]
            : []
        }
        loading={loading && cats.length === 0}
        // Zone 5 en chargement : skeleton fidele a l'onglet actif (zero saut de
        // mise en page). "Au refuge" = la ligne de liste standard ; "En sortie" =
        // la meme ligne plus l'amas de droite (retour / statut / bouton).
        skeleton={section === 'outside' ? <OutsideSkeletonRows /> : <SkeletonListRows />}
        error={error}
        errorTitle="Echec du chargement"
        onRetry={() => void load()}
        retryLabel="Reessayer"
        empty={emptyBySection[section]}
        emptyTitle={emptyTitleBySection[section]}
        emptyHint={emptyHintBySection[section]}
      >
        {/* Onglet 1 : les chats au refuge. */}
        {section === 'inside' && catsInside.length > 0 && (
          <ul>
            {visibleInside.length === 0 && (
              <li className="px-5 py-4 text-sm text-ink-500">
                Aucun chat au refuge ne correspond a "{query}".
              </li>
            )}
            {visibleInside.map((cat) => (
              <li
                key={cat.id}
                className="flex items-center gap-3.5 border-b border-border-soft px-5 py-2.5 hover:bg-surface-1"
              >
                <Avatar size={30} initials={cat.name.slice(0, 2)} />
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink-900">{cat.name}</span>
                  <span className="block truncate text-xs text-ink-500">Au refuge</span>
                </div>
                <StatusBadge tone="success" label="Present" />
              </li>
            ))}
          </ul>
        )}

        {/* Onglet 2 : les chats en sortie. */}
        {section === 'outside' && catsOutside.length > 0 && (
          <ul>
            {visibleOutside.length === 0 && (
              <li className="px-5 py-4 text-sm text-ink-500">
                Aucun chat en sortie ne correspond a "{query}".
              </li>
            )}
            {visibleOutside.map((cat) => {
              const outing = ongoingByCatId.get(cat.id);
              if (outing == null) {
                return null;
              }
              const late = isOverdue(outing, now);
              const person =
                outing.personName !== ''
                  ? outing.personPhone !== ''
                    ? `${outing.personName} (${outing.personPhone})`
                    : outing.personName
                  : 'Personne non renseignee';
              return (
                <li
                  key={cat.id}
                  className="flex items-center gap-3.5 border-b border-border-soft px-5 py-2.5 hover:bg-surface-1"
                >
                  <Avatar size={30} initials={cat.name.slice(0, 2)} />
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink-900">
                      {cat.name}
                    </span>
                    <span className="block truncate text-xs text-ink-500">
                      {reasonLabelOf(reasons, outing.reason)} - sorti le{' '}
                      {formatDateTime(outing.leftAt)} - {person}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2.5">
                    <span className="text-xs text-ink-500">
                      Retour prevu : {formatDateTime(outing.dueBackAt)}
                    </span>
                    <StatusBadge
                      tone={late ? 'accent' : 'neutral'}
                      label={late ? 'En retard' : 'En sortie'}
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={returningId === outing.id}
                      disabled={returningId != null && returningId !== outing.id}
                      onClick={() => void handleMarkReturned(outing)}
                    >
                      <IconCheck size={14} />
                      Marquer rentre
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </ScreenLayout>

      {/* Modal : nouvelle sortie (regle G6 : jamais en zone centrale). */}
      <Modal
        open={openModal === 'outing'}
        onClose={closeModal}
        title="Nouvelle sortie"
        size="lg"
        footer={
          <>
            <Button size="sm" variant="secondary" disabled={saving} onClick={closeModal}>
              Annuler
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={saving}
              onClick={() => void handleSaveOuting()}
            >
              Enregistrer la sortie
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Chat qui sort</span>
            {catsInside.length === 0 ? (
              <p className="text-xs text-ink-500">Aucun chat disponible au refuge.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {catsInside.map((cat) => (
                  <Button
                    key={cat.id}
                    size="sm"
                    variant={cat.id === formCatId ? 'primary' : 'secondary'}
                    onClick={() => setFormCatId(cat.id)}
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Motif</span>
            <div className="flex flex-wrap gap-2">
              {reasons.map((reason) => (
                <Button
                  key={reason.value}
                  size="sm"
                  variant={reason.value === formReason ? 'primary' : 'secondary'}
                  onClick={() => setFormReason(reason.value)}
                >
                  {reason.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="outing-left-date" className="text-xs font-medium text-ink-500">
                Date de sortie
              </label>
              <Input
                id="outing-left-date"
                type="date"
                className="h-10"
                value={formLeftDate}
                onChange={(event) => setFormLeftDate(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="outing-left-time" className="text-xs font-medium text-ink-500">
                Heure de sortie
              </label>
              <Input
                id="outing-left-time"
                type="time"
                className="h-10"
                value={formLeftTime}
                onChange={(event) => setFormLeftTime(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="outing-back-date" className="text-xs font-medium text-ink-500">
                Date de retour prevue
              </label>
              <Input
                id="outing-back-date"
                type="date"
                className="h-10"
                value={formBackDate}
                onChange={(event) => setFormBackDate(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="outing-back-time" className="text-xs font-medium text-ink-500">
                Heure de retour prevue
              </label>
              <Input
                id="outing-back-time"
                type="time"
                className="h-10"
                value={formBackTime}
                onChange={(event) => setFormBackTime(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="outing-person" className="text-xs font-medium text-ink-500">
                Personne qui emmene
              </label>
              <Input
                id="outing-person"
                type="text"
                className="h-10"
                placeholder="Optionnel"
                value={formPersonName}
                onChange={(event) => setFormPersonName(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="outing-phone" className="text-xs font-medium text-ink-500">
                Telephone
              </label>
              <Input
                id="outing-phone"
                type="tel"
                className="h-10"
                placeholder="Optionnel"
                value={formPersonPhone}
                onChange={(event) => setFormPersonPhone(event.target.value)}
              />
            </div>
          </div>

          {saveError != null && <p className="text-xs text-danger">{saveError}</p>}
        </div>
      </Modal>

      {/* Modal : nouveau chat (onglet 1). */}
      <Modal
        open={openModal === 'cat'}
        onClose={closeModal}
        title="Ajouter un chat"
        size="sm"
        footer={
          <>
            <Button size="sm" variant="secondary" disabled={saving} onClick={closeModal}>
              Annuler
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={saving}
              onClick={() => void handleSaveCat()}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cat-name" className="text-xs font-medium text-ink-500">
              Nom du chat
            </label>
            <Input
              id="cat-name"
              type="text"
              className="h-10"
              value={formCatName}
              onChange={(event) => setFormCatName(event.target.value)}
            />
          </div>

          <p className="text-xs text-ink-500">
            La fiche est creee au refuge et devient visible dans Cat Manager Six.
          </p>

          {saveError != null && <p className="text-xs text-danger">{saveError}</p>}
        </div>
      </Modal>
    </>
  );
}

export default CatLoggerOutsideScreen;
